/**
 * Premium subscription upsell modal.
 *
 * Shows a comparison of free vs. premium tiers and handles the Stripe checkout
 * flow. Also serves as the rate-limit wall: when a free user exceeds 50 API
 * calls in 30 days, this modal blocks further use until they subscribe or the
 * rolling window resets.
 */
import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import "./PremiumModal.css";

const PremiumModal = ({ isOpen, onClose, isRateLimited = false, rateLimitInfo = null }) => {
  const { user, signInWithGoogle, isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Initialize Stripe (you'll need to add your publishable key)
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

  const freeFeatures = [
    "50 website uses per month",
    "Time and skin type adjustments"
  ];

  const premiumFeatures = [
    "Unlimited website use",
    "Reapplication times",
    "Future forecasting",
    "Personalized burn factors",
    "Environment and altitude adjustments",
    "Push notifications to reapply",
    "iOS app with lock screen widget",
    "Skin type quiz",
  ];

  const handleSignIn = async () => {
    setIsSubmitting(true);
    setError("");
    
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error("Error signing in:", err);
      setError("Failed to sign in. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated || !user) {
      setError("Please sign in to continue");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Call Firebase Function to create checkout session
      const response = await fetch(`https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'do-i-need-sunscreen'}.cloudfunctions.net/createCheckoutSession`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          priceId: process.env.REACT_APP_STRIPE_PRICE_ID || 'price_1234567890' // You'll replace this with your actual price ID
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { sessionId } = await response.json();
      const stripe = await stripePromise;
      
      // Redirect to Stripe Checkout
      const result = await stripe.redirectToCheckout({ sessionId });
      
      if (result.error) {
        setError(result.error.message);
      }
      
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError("Failed to start checkout. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClose = () => {
    // Don't allow closing when rate limited
    if (isRateLimited) {
      return;
    }
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  // Helper function to format the next available date
  const formatNextAvailableDate = (dateString) => {
    if (!dateString) return "soon";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {!isRateLimited && (
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        )}

        {isRateLimited ? (
          <>
            {/* Rate Limited Modal Content */}
            <div className="modal-header rate-limited-header">
              <div className="rate-limit-x">✖</div>
              <h2>You've reached the free limit!</h2>
              <p>Subscribe for unlimited website uses.</p>
              {rateLimitInfo?.nextAvailableDate && (
                <p className="next-available-date">
                  More free website uses available on {formatNextAvailableDate(rateLimitInfo.nextAvailableDate)}
                </p>
              )}
            </div>

            {/* Free Tier Features */}
            <div className="features-section">
              <h3>Free tier</h3>
              <div className="features-list">
                {freeFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="checkmark free-checkmark">✓</span>
                    <span className="feature-text">{feature}</span>
                  </div>
                ))}
              </div>
            </div>


            {/* Premium Tier Features */}
            <div className="features-section">
              <h3>Premium tier</h3>
              <div className="features-list">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="checkmark premium-checkmark">✓</span>
                    <span className="feature-text">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Normal Modal Content */}
            <div className="modal-header">
              <h2>DoINeedSunscreenRightNow+</h2>
              <p>Get access to premium features</p>
            </div>

            {/* Free Tier Features */}
            <div className="features-section">
              <h3>Free tier</h3>
              <div className="features-list">
                {freeFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="checkmark free-checkmark">✓</span>
                    <span className="feature-text">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Tier Features */}
            <div className="features-section">
              <h3>Premium tier</h3>
              <div className="features-list">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="checkmark premium-checkmark">✓</span>
                    <span className="feature-text">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="action-section">
          {error && <div className="error-message">{error}</div>}
          
          {!isAuthenticated ? (
            <div className="auth-section">
              <p className="auth-prompt">Sign in to get premium access</p>
              <button
                onClick={handleSignIn}
                className="submit-button google-signin"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing in..." : "Sign in with Google"}
              </button>
            </div>
          ) : (
            <div className="checkout-section">
              <div className="user-welcome">
                <p className="pricing">Subscribe to get premium access</p>
              </div>
              
              <button
                onClick={handleCheckout}
                className="submit-button checkout"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Subscribe now - $2.99/month"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumModal;
